import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getAllProducts, createTailoringOrder, createCustomer, getRecentCustomers, formatPhone } from '../lib/api';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { toast } from 'sonner';

const toNumber = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const money = (n) => Number(n || 0).toLocaleString('es-DO', { style: 'currency', currency: 'DOP' });

const isService = (product) => {
  const type = String(product?.product_type || product?.type || '').trim().toUpperCase();
  return type === 'SERVICIO' || type === 'SERVICE' || product?.track_inventory === false || product?.track_inventory === 'false' || product?.track_inventory === 0;
};

const defaultDueDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
};

const emptySubOrderForm = {
  product_id: '',
  product_name: '',
  item_price: '',
  customization: '',
  measurements: { pecho: '', cintura: '', cadera: '', largo: '', manga: '' },
  changes_requested: '',
  services: [],
  work_notes: '',
};

const TailoringOrderNew = () => {
  const navigate = useNavigate();
  const [products, setProducts]   = useState([]);
  const [saving, setSaving]       = useState(false);

  // ── Customer ──────────────────────────────────────────────────────────
  const [recentCustomers, setRecentCustomers]           = useState([]);
  const [customerSearch, setCustomerSearch]             = useState('');
  const [customerResults, setCustomerResults]           = useState([]);
  const [customerSearching, setCustomerSearching]       = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showNewCustomerForm, setShowNewCustomerForm]   = useState(false);
  const [newCustomerData, setNewCustomerData]           = useState({ name: '', last_name: '', phone: '', email: '' });
  const [phoneWarning, setPhoneWarning]                 = useState(null);

  // ── Main form ─────────────────────────────────────────────────────────
  const [form, setForm] = useState({ customer_id: '', due_date: defaultDueDate(), initial_payment: '', notes: '' });
  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Sub-orders ────────────────────────────────────────────────────────
  const [subOrders, setSubOrders]               = useState([]);
  const [showSubOrderForm, setShowSubOrderForm] = useState(false);
  const [editingIdx, setEditingIdx]             = useState(null);
  const [soForm, setSoForm]                     = useState({ ...emptySubOrderForm });
  const setSoField = (k, v)                     => setSoForm(p => ({ ...p, [k]: v }));
  const setSoMeas  = (k, v)                     => setSoForm(p => ({ ...p, measurements: { ...p.measurements, [k]: v } }));
  const [soSvcId, setSoSvcId] = useState('');
  const [showCustomization, setShowCustomization] = useState(false);

  // ── Derived ───────────────────────────────────────────────────────────
  const baseProducts     = useMemo(() => products.filter(p => !isService(p)), [products]);
  const serviceProducts  = useMemo(() => products.filter(p => isService(p)),  [products]);
  const totalAmount      = useMemo(() => subOrders.reduce((s, so) => s + so.subtotal, 0), [subOrders]);
  const confectionTotal  = useMemo(() => subOrders.reduce((s, so) => s + toNumber(so.item_price), 0), [subOrders]);
  const servicesTotalAll = useMemo(() => subOrders.reduce((s, so) => s + so.services_total, 0), [subOrders]);
  const soItemPrice      = useMemo(() => toNumber(soForm.item_price) + toNumber(soForm.customization), [soForm.item_price, soForm.customization]);
  const soServicesTotal  = useMemo(() => soForm.services.reduce((s, sv) => s + sv.subtotal, 0), [soForm.services]);
  const soSubtotal       = useMemo(() => soItemPrice + soServicesTotal, [soItemPrice, soServicesTotal]);

  useEffect(() => {
    (async () => {
      try {
        const prods = await getAllProducts();
        setProducts(Array.isArray(prods) ? prods : (prods?.products || []));
      } catch (e) {
        toast.error(e?.response?.data?.error || 'Error cargando productos');
      }
    })();
    getRecentCustomers().then(data => setRecentCustomers(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchCustomers(customerSearch), 350);
    return () => clearTimeout(t);
  }, [customerSearch]); // eslint-disable-line

  const searchCustomers = async (q) => {
    if (!q || q.length < 2) { setCustomerResults([]); return; }
    setCustomerSearching(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers?q=${encodeURIComponent(q)}&per_page=8`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCustomerResults(data.customers || data || []);
    } catch { setCustomerResults([]); }
    finally { setCustomerSearching(false); }
  };

  const [selectedCustomerData, setSelectedCustomerData] = React.useState(null);

  const selectCustomer = (c) => {
    setField('customer_id', c.id);
    setCustomerSearch(`${c.name} ${c.last_name}`);
    setShowCustomerDropdown(false);
    setCustomerResults([]);
    setSelectedCustomerData(c);
  };

  const handleCreateCustomer = async (confirmDuplicate = false) => {
    if (!newCustomerData.name || !newCustomerData.phone) {
      toast.error('Nombre y teléfono son requeridos'); return;
    }
    if (!isValidPhoneNumber(newCustomerData.phone)) {
      toast.error('Número de teléfono inválido'); return;
    }
    try {
      const payload = confirmDuplicate ? { ...newCustomerData, confirm_duplicate_phone: true } : newCustomerData;
      const created = await createCustomer(payload);
      selectCustomer(created);
      setShowNewCustomerForm(false);
      setPhoneWarning(null);
      setNewCustomerData({ name: '', last_name: '', phone: '', email: '' });
      toast.success('Cliente creado');
      getRecentCustomers().then(data => setRecentCustomers(Array.isArray(data) ? data : [])).catch(() => {});
    } catch (e) {
      const res = e?.response?.data;
      if (e?.response?.status === 409 && res?.warning === 'duplicate_phone') {
        setPhoneWarning(res.message); return;
      }
      toast.error(res?.error || 'Error creando cliente');
    }
  };

  // ── Sub-order handlers ────────────────────────────────────────────────
  const handleSoProductChange = (productId) => {
    const prod = baseProducts.find(p => p.id === productId);
    setSoForm(p => ({
      ...p,
      product_id:   productId,
      product_name: prod?.name || '',
      item_price:   String(Number(prod?.price || 0)),
      customization: '',
    }));
  };

  const addSoService = () => {
    const svc = serviceProducts.find(p => p.id === soSvcId);
    if (!svc) return toast.error('Selecciona un servicio');
    const qty = 1;
    const unitPrice = Number(svc.price || 0);
    setSoForm(p => {
      const existing = p.services.find(s => s.product_id === svc.id);
      const next = existing
        ? p.services.map(s => s.product_id !== svc.id ? s : { ...s, quantity: s.quantity + qty, subtotal: (s.quantity + qty) * s.unit_price })
        : [...p.services, { product_id: svc.id, name: svc.name, quantity: qty, unit_price: unitPrice, subtotal: qty * unitPrice }];
      return { ...p, services: next };
    });
    setSoSvcId('');
  };

  const removeSoService = (productId) =>
    setSoForm(p => ({ ...p, services: p.services.filter(s => s.product_id !== productId) }));

  const saveSubOrder = () => {
    if (!soForm.product_id)                        return toast.error('Selecciona un producto base');
    if (!soForm.item_price || toNumber(soForm.item_price) <= 0) return toast.error('El costo de confección debe ser mayor que 0');
    if (!soForm.changes_requested.trim())          return toast.error('Los cambios solicitados son obligatorios');
    const services_total   = soForm.services.reduce((s, sv) => s + sv.subtotal, 0);
    const effective_price  = toNumber(soForm.item_price) + toNumber(soForm.customization);
    const subtotal         = effective_price + services_total;
    const entry            = { ...soForm, item_price: effective_price, customization: toNumber(soForm.customization), services_total, subtotal };

    if (editingIdx !== null) {
      setSubOrders(p => p.map((so, i) => i === editingIdx ? entry : so));
    } else {
      setSubOrders(p => [...p, entry]);
    }
    setSoForm({ ...emptySubOrderForm });
    setSoSvcId('');
    setShowCustomization(false);
    setShowSubOrderForm(false);
    setEditingIdx(null);
  };

  const editSubOrder = (idx) => {
    const so = subOrders[idx];
    setSoForm({ ...so, measurements: { ...so.measurements } });
    setShowCustomization(toNumber(so.customization) > 0);
    setEditingIdx(idx);
    setShowSubOrderForm(true);
  };

  const duplicateSubOrder = (idx) => {
    setSubOrders(p => [...p, { ...p[idx] }]);
    toast.success('Prenda duplicada');
  };

  const removeSubOrder = (idx) => setSubOrders(p => p.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.customer_id)    return toast.error('Selecciona un cliente');
    if (subOrders.length === 0) return toast.error('Agrega al menos una prenda');

    const anticipo   = parseFloat(form.initial_payment) || 0;
    const minPayment = totalAmount * 0.5;
    if (anticipo <= 0)          return toast.error('Debes ingresar el anticipo para crear el pedido');
    if (anticipo < minPayment)  return toast.error(`El anticipo mínimo es ${money(minPayment)} (50% de ${money(totalAmount)})`);

    try {
      setSaving(true);
      const payload = {
        customer_id:       form.customer_id,
        delivery_due_date: form.due_date,
        notes:             form.notes,
        initial_payment:   anticipo,
        sub_orders: subOrders.map(so => ({
          product_id:        so.product_id,
          item_price:        so.item_price,
          measurements:      so.measurements,
          changes_requested: so.changes_requested,
          services:          so.services.map(s => ({ product_id: s.product_id, quantity: s.quantity, unit_price: s.unit_price })),
          work_notes:        so.work_notes,
        })),
      };
      const created = await createTailoringOrder(payload);
      toast.success('Pedido creado — abriendo POS para cobrar el anticipo');
      navigate(`/pos?tailoring_order_id=${created.id}&initial_payment=${anticipo.toFixed(2)}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error creando pedido');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 space-y-6 max-w-4xl mx-auto" data-testid="tailoring-new-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Nuevo Pedido de Confección</h1>
        <p className="text-slate-600">El pedido queda en espera de producción. Sucursal y vendedor se toman del usuario logueado.</p>
      </div>

      {/* Cliente */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Cliente</CardTitle></CardHeader>
        <CardContent>
          <div className="relative">
            <Label>Buscar cliente</Label>
            <div className="relative mt-1">
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); if (!e.target.value) setField('customer_id', ''); }}
                onFocus={() => customerSearch.length >= 2 && setShowCustomerDropdown(true)}
                onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                placeholder="Buscar por nombre o teléfono..."
                className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 ${form.customer_id ? 'border-cyan-400 bg-cyan-50' : 'border-slate-200'}`}
              />
              {customerSearching && <span className="absolute right-3 top-2.5 text-xs text-slate-400">...</span>}
              {form.customer_id  && <span className="absolute right-3 top-2.5 text-cyan-500 text-xs">✓</span>}
            </div>

            {selectedCustomerData?.has_logo && (
              <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                <span>🖼️</span>
                <div>
                  <span className="font-medium">Logo disponible</span>
                  {selectedCustomerData.logo_notes && <span className="text-emerald-600 ml-2 text-xs">{selectedCustomerData.logo_notes}</span>}
                </div>
              </div>
            )}

            {showCustomerDropdown && customerResults.length > 0 && (
              <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-52 overflow-y-auto">
                {customerResults.map(c => (
                  <button key={c.id} type="button" onMouseDown={() => selectCustomer(c)}
                    className="w-full text-left px-3 py-2 hover:bg-cyan-50 text-sm border-b border-slate-100 last:border-0">
                    <span className="font-medium">{c.name} {c.last_name}</span>
                    <span className="text-slate-400 ml-2 text-xs">{formatPhone(c.phone)}</span>
                  </button>
                ))}
                <button type="button" onMouseDown={() => { setShowCustomerDropdown(false); setShowNewCustomerForm(true); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-cyan-600 font-medium flex items-center gap-1">
                  <span>+</span> Crear nuevo cliente
                </button>
              </div>
            )}

            {showCustomerDropdown && customerSearch.length >= 2 && customerResults.length === 0 && !customerSearching && (
              <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1">
                <button type="button" onMouseDown={() => { setShowCustomerDropdown(false); setShowNewCustomerForm(true); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-cyan-600 font-medium flex items-center gap-1">
                  <span>+</span> No encontrado — Crear cliente
                </button>
              </div>
            )}

            {showNewCustomerForm && (
              <div className="mt-2 p-3 border border-cyan-200 bg-cyan-50 rounded-lg space-y-2">
                <p className="text-xs font-semibold text-cyan-800">Nuevo cliente</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input placeholder="Nombre *" value={newCustomerData.name}
                    onChange={e => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                    className="border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                  <input placeholder="Apellido" value={newCustomerData.last_name}
                    onChange={e => setNewCustomerData({ ...newCustomerData, last_name: e.target.value })}
                    className="border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                  <div className="phone-input-container">
                    <PhoneInput international defaultCountry="DO"
                      value={newCustomerData.phone}
                      onChange={v => setNewCustomerData({ ...newCustomerData, phone: v || '' })} />
                  </div>
                  <input placeholder="Email" value={newCustomerData.email}
                    onChange={e => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                    className="border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                </div>
                {phoneWarning && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 space-y-2">
                    <div className="flex items-start gap-1.5">
                      <span className="text-base">⚠️</span>
                      <div>
                        <p className="font-semibold text-amber-900 text-xs">Teléfono duplicado</p>
                        <p className="text-xs text-amber-800">{phoneWarning}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setPhoneWarning(null)}
                        className="flex-1 text-xs border border-slate-200 rounded py-1 hover:bg-slate-50">Cancelar</button>
                      <button type="button" onClick={() => { setPhoneWarning(null); handleCreateCustomer(true); }}
                        className="flex-1 text-xs bg-amber-500 hover:bg-amber-600 text-white rounded py-1 font-medium">Guardar igual</button>
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <button type="button" onClick={() => handleCreateCustomer(false)}
                    className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-medium py-1.5 rounded transition-colors">
                    Crear y seleccionar
                  </button>
                  <button type="button" onClick={() => { setShowNewCustomerForm(false); setPhoneWarning(null); }}
                    className="px-3 text-slate-500 hover:text-slate-700 text-xs border border-slate-200 rounded py-1.5">
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>

          {recentCustomers.length > 0 && !form.customer_id && !customerSearch && (
            <div className="mt-3">
              <p className="text-xs text-slate-400 mb-1.5">Recientes:</p>
              <div className="flex flex-wrap gap-1.5">
                {recentCustomers.map(c => (
                  <button key={c.id} type="button" onClick={() => selectCustomer(c)}
                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 hover:bg-cyan-100 hover:text-cyan-800 transition-colors border border-slate-200 hover:border-cyan-300">
                    {c.name} {c.last_name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Prendas (sub-pedidos) */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-lg">Prendas</CardTitle>
            {subOrders.length > 0 && (
              <p className="text-sm text-slate-500 mt-0.5">
                {subOrders.length} prenda{subOrders.length > 1 ? 's' : ''} — {money(totalAmount)}
              </p>
            )}
          </div>
          {!showSubOrderForm && subOrders.length > 0 && (
            <Button type="button" size="sm"
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              onClick={() => { setSoForm({ ...emptySubOrderForm }); setEditingIdx(null); setShowSubOrderForm(true); }}>
              + Agregar prenda
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">

          {/* Lista de prendas agregadas */}
          {subOrders.map((so, idx) => (
            <div key={idx} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-cyan-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <div>
                    <p className="font-medium text-sm">{so.product_name}</p>
                    <p className="text-xs text-slate-500">
                      Confección: {money(so.item_price)}
                      {so.services_total > 0 && ` + servicios: ${money(so.services_total)}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="font-semibold text-sm text-slate-700">{money(so.subtotal)}</span>
                  <div className="flex gap-2 text-xs">
                    <button type="button" onClick={() => editSubOrder(idx)}
                      className="text-slate-400 hover:text-slate-700 font-medium">Editar</button>
                    <button type="button" onClick={() => duplicateSubOrder(idx)}
                      className="text-cyan-400 hover:text-cyan-600 font-medium">Duplicar</button>
                    <button type="button" onClick={() => removeSubOrder(idx)}
                      className="text-rose-400 hover:text-rose-600 font-medium">Quitar</button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {subOrders.length === 0 && !showSubOrderForm && (
            <button type="button"
              onClick={() => { setSoForm({ ...emptySubOrderForm }); setEditingIdx(null); setShowSubOrderForm(true); }}
              className="w-full border-2 border-dashed border-slate-300 rounded-lg py-6 text-slate-400 hover:border-cyan-400 hover:text-cyan-600 transition-colors text-sm font-medium">
              + Agregar primera prenda
            </button>
          )}

          {subOrders.length > 0 && !showSubOrderForm && (
            <button type="button"
              onClick={() => { setSoForm({ ...emptySubOrderForm }); setEditingIdx(null); setShowSubOrderForm(true); }}
              className="w-full border border-dashed border-slate-200 rounded-lg py-3 text-slate-400 hover:border-cyan-400 hover:text-cyan-500 transition-colors text-sm">
              + Agregar otra prenda
            </button>
          )}

          {/* Formulario de prenda */}
          {showSubOrderForm && (
            <div className="border-2 border-cyan-200 rounded-xl p-4 bg-cyan-50 space-y-4">
              <p className="text-sm font-semibold text-cyan-800">
                {editingIdx !== null
                  ? `Editando prenda ${String.fromCharCode(65 + editingIdx)}`
                  : `Nueva prenda ${String.fromCharCode(65 + subOrders.length)}`}
              </p>

              {/* Producto + costo */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Modelo base *</Label>
                  <Select value={soForm.product_id} onValueChange={handleSoProductChange}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Selecciona producto" /></SelectTrigger>
                    <SelectContent>
                      {baseProducts.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Precio Base</Label>
                  <Input
                    type="number" readOnly
                    value={soForm.item_price}
                    placeholder="0.00"
                    className="bg-slate-50 text-slate-500 cursor-not-allowed"
                  />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 h-6">
                    <input
                      type="checkbox"
                      id="chk-customization"
                      checked={showCustomization}
                      onChange={e => {
                        setShowCustomization(e.target.checked);
                        if (!e.target.checked) setSoField('customization', '');
                      }}
                      className="w-4 h-4 accent-cyan-500 cursor-pointer"
                    />
                    <label htmlFor="chk-customization" className="text-sm font-medium text-slate-700 cursor-pointer">
                      Personalización
                    </label>
                  </div>
                  {showCustomization && (
                    <Input
                      type="number" min="0" step="0.01"
                      value={soForm.customization}
                      onChange={e => setSoField('customization', e.target.value)}
                      placeholder="0.00"
                      className="bg-white"
                      autoFocus
                    />
                  )}
                </div>
              </div>

              {/* Medidas */}
              <div>
                <Label className="mb-2 block">Medidas (cm)</Label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {['pecho', 'cintura', 'cadera', 'largo', 'manga'].map(k => (
                    <div key={k} className="space-y-1">
                      <label className="text-xs text-slate-500 capitalize">{k}</label>
                      <Input
                        type="number" min="0" step="0.1"
                        value={soForm.measurements[k]}
                        onChange={e => setSoMeas(k, e.target.value)}
                        placeholder="0"
                        className="bg-white"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Cambios solicitados */}
              <div className="space-y-1">
                <Label>Cambios solicitados <span className="text-rose-500 text-xs">*</span></Label>
                <Textarea
                  value={soForm.changes_requested}
                  onChange={e => setSoField('changes_requested', e.target.value)}
                  placeholder="Describe los cambios o ajustes solicitados..."
                  className="bg-white text-sm"
                  rows={2}
                />
              </div>

              {/* Servicios adicionales */}
              <div className="space-y-2">
                <Label>Servicios</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Select value={soSvcId} onValueChange={setSoSvcId}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Selecciona servicio" /></SelectTrigger>
                      <SelectContent>
                        {serviceProducts.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name} — {money(s.price)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addSoService} className="bg-white px-6">
                    Agregar
                  </Button>
                </div>
                {soForm.services.length > 0 && (
                  <div className="space-y-1">
                    {soForm.services.map(s => (
                      <div key={s.product_id} className="border border-slate-200 rounded bg-white overflow-hidden">
                        <div className="flex items-center justify-between text-sm px-2 py-1.5">
                          <span>{s.name}{s.quantity > 1 && <span className="ml-1 text-xs text-slate-400 font-normal">×{s.quantity}</span>}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{money(s.subtotal)}</span>
                            <button type="button" onClick={() => removeSoService(s.product_id)}
                              className="text-rose-400 hover:text-rose-600 text-xs">✕</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <Label>Notas <span className="text-slate-400 font-normal text-xs">(opcional)</span></Label>
                <Textarea
                  value={soForm.work_notes}
                  onChange={e => setSoField('work_notes', e.target.value)}
                  placeholder="Instrucciones específicas para esta prenda..."
                  className="bg-white text-sm"
                  rows={2}
                />
              </div>

              {soSubtotal > 0 && (
                <div className="text-right text-sm text-slate-600">
                  {soItemPrice > 0 && <span className="mr-4">Confección: {money(soItemPrice)}</span>}
                  {soServicesTotal > 0 && <span className="mr-4">Servicios: {money(soServicesTotal)}</span>}
                  <span className="font-bold text-slate-900">Subtotal: {money(soSubtotal)}</span>
                </div>
              )}

              <div className="flex justify-center gap-2 pt-1">
                <Button type="button" onClick={saveSubOrder} className="bg-cyan-500 hover:bg-cyan-600 text-white w-40 h-11">
                  {editingIdx !== null ? 'Guardar cambios' : 'Agregar prenda'}
                </Button>
                <Button type="button" variant="outline" className="w-40 h-11"
                  onClick={() => { setShowSubOrderForm(false); setEditingIdx(null); setSoForm({ ...emptySubOrderForm }); setShowCustomization(false); }}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fecha de entrega */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Fecha de entrega</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-1 max-w-xs">
            <Input type="date" value={form.due_date} onChange={e => setField('due_date', e.target.value)} />
            <p className="text-xs text-slate-500">Si no se cambia, queda a 7 días.</p>
          </div>
        </CardContent>
      </Card>

      {/* Notas generales */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Notas generales <span className="text-sm font-normal text-slate-400">(opcional)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={form.notes}
            onChange={e => setField('notes', e.target.value)}
            placeholder="Observaciones generales del pedido..."
            rows={3}
          />
        </CardContent>
      </Card>

      {/* Resumen de pago */}
      {totalAmount > 0 && (() => {
        const minPayment  = totalAmount * 0.5;
        const anticipo    = parseFloat(form.initial_payment) || 0;
        const balance     = totalAmount - anticipo;
        const isValid     = anticipo >= minPayment;
        const hasAnticipo = anticipo > 0;
        return (
          <Card className={`border-2 ${isValid ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">💰 Resumen de pago</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
                  <p className="text-xs text-slate-500 mb-1">Total confección</p>
                  <p className="text-xl font-bold text-slate-700" style={{ fontFamily: 'JetBrains Mono' }}>{money(confectionTotal)}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
                  <p className="text-xs text-slate-500 mb-1">Servicios adicionales</p>
                  <p className="text-xl font-bold text-slate-700" style={{ fontFamily: 'JetBrains Mono' }}>{money(servicesTotalAll)}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border-2 border-slate-900 text-center">
                  <p className="text-xs text-slate-500 mb-1">Total general</p>
                  <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>{money(totalAmount)}</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 border border-amber-200 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Anticipo mínimo requerido (50%)</span>
                  <span className="font-bold text-amber-600" style={{ fontFamily: 'JetBrains Mono' }}>{money(minPayment)}</span>
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-slate-500">Anticipo a cobrar *</label>
                  <div className="flex gap-2">
                    {[
                      { label: '50% mín.', value: totalAmount * 0.5 },
                      { label: '75%',      value: totalAmount * 0.75 },
                      { label: '100%',     value: totalAmount },
                    ].map(opt => (
                      <button
                        key={opt.label}
                        type="button"
                        onClick={() => setField('initial_payment', opt.value.toFixed(2))}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          parseFloat(form.initial_payment) === parseFloat(opt.value.toFixed(2))
                            ? 'bg-cyan-500 text-white border-cyan-500'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-cyan-400 hover:text-cyan-600'
                        }`}
                      >
                        {opt.label}<br />
                        <span className="font-mono">{money(opt.value)}</span>
                      </button>
                    ))}
                  </div>
                  <input
                    type="number" min="0" step="0.01"
                    value={form.initial_payment}
                    onChange={e => setField('initial_payment', e.target.value)}
                    placeholder={`Mínimo ${money(minPayment)}`}
                    className={`w-full border-2 rounded-lg px-3 py-2 text-lg font-mono focus:outline-none focus:ring-2 ${
                      isValid     ? 'border-green-400 focus:ring-green-400 bg-green-50' :
                      hasAnticipo ? 'border-rose-400 focus:ring-rose-400 bg-rose-50'   :
                                    'border-slate-200 focus:ring-cyan-400'
                    }`}
                  />
                  {hasAnticipo && !isValid && (
                    <p className="text-xs text-rose-600 font-medium">⚠️ El anticipo debe ser mínimo {money(minPayment)} — faltan {money(minPayment - anticipo)}</p>
                  )}
                  {isValid && (
                    <p className="text-xs text-green-600 font-medium">✅ Anticipo válido — balance restante: {money(balance)}</p>
                  )}
                  {!hasAnticipo && (
                    <p className="text-xs text-amber-700">El pedido no se puede crear sin el pago del 50% mínimo.</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      <div className="flex gap-2">
        <Button onClick={submit} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar pedido'}
        </Button>
        <Button variant="outline" onClick={() => navigate('/tailoring-orders')} disabled={saving}>
          Cancelar
        </Button>
      </div>

      <style>{`
        .phone-input-container .PhoneInput {
          display: flex; align-items: center; height: 34px;
          border: 1px solid #e2e8f0; border-radius: 6px;
          background: white; padding: 0 8px; gap: 6px; font-size: 14px;
        }
        .phone-input-container .PhoneInput:focus-within {
          border-color: #06b6d4;
          box-shadow: 0 0 0 2px rgba(6,182,212,0.25);
        }
        .phone-input-container .PhoneInputInput {
          flex: 1; border: none; outline: none;
          background: transparent; font-size: 13px; color: #0f172a;
        }
        .phone-input-container .PhoneInputCountrySelect {
          border: none; background: transparent; cursor: pointer;
        }
        .phone-input-container .PhoneInputCountryIcon { width: 18px; height: 13px; }
      `}</style>
    </div>
  );
};

export default TailoringOrderNew;
